import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Play, Pause, SkipForward, SkipBack, Loader2, Headphones,
  List, BookOpen, Repeat, Plus, Trash2, Pencil, Save, X, 
  Volume2, VolumeX, Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'es', name: 'Spanish' }, { code: 'pt', name: 'Portuguese' },
  { code: 'fr', name: 'French' }, { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' }, { code: 'en', name: 'English' },
  { code: 'ru', name: 'Russian' }, { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' }, { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' }, { code: 'tr', name: 'Turkish' },
  { code: 'hi', name: 'Hindi' }, { code: 'he', name: 'Hebrew' },
  { code: 'fa', name: 'Persian' }, { code: 'sw', name: 'Swahili' },
  { code: 'th', name: 'Thai' }, { code: 'vi', name: 'Vietnamese' },
  { code: 'id', name: 'Indonesian' }, { code: 'ps', name: 'Pashto' },
];

const formatTime = (secs) => {
  if (!secs || isNaN(secs)) return '0:00';
  return `${Math.floor(secs / 60)}:${String(Math.floor(secs % 60)).padStart(2, '0')}`;
};

export default function ListeningPage() {
  const navigate = useNavigate();
  const { languageCode: langParam } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.is_admin === true;
  const [lang, setLang] = useState(langParam || 'es');

  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const audioRef = useRef(null);
  const progressRef = useRef(null);

  // Playlists
  const [playlists, setPlaylists] = useState(() => {
    try { return JSON.parse(localStorage.getItem('listening_playlists') || '[]'); } catch { return []; }
  });
  const [activePlaylist, setActivePlaylist] = useState(null);
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [playlistName, setPlaylistName] = useState('');
  const [selectedForPlaylist, setSelectedForPlaylist] = useState(new Set());

  // Autoplay — when the current list finishes, start again from the top (loop).
  const [autoplay, setAutoplay] = useState(() => {
    return localStorage.getItem('listening_autoplay') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('listening_autoplay', autoplay ? 'true' : 'false');
  }, [autoplay]);

  useEffect(() => {
    fetchEpisodes();
  }, [lang]);

  useEffect(() => {
    if (langParam && langParam !== lang) setLang(langParam);
  }, [langParam]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => stopCurrentAudio();
  }, []);

  const savePlaylists = (updated) => {
    setPlaylists(updated);
    localStorage.setItem('listening_playlists', JSON.stringify(updated));
  };

  const fetchEpisodes = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/forge-stories/listening/${lang}`);
      setEpisodes(resp.data.episodes || []);
    } catch { setEpisodes([]); }
    finally { setLoading(false); }
  };

  const updateProgress = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.duration && !isNaN(audio.duration)) {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    }
    if (audioRef.current && !audioRef.current.paused) {
      progressRef.current = requestAnimationFrame(updateProgress);
    }
  }, []);

  // Use a ref for playlist auto-advance to avoid stale closures
  const playlistRef = useRef({ activePlaylist, playlistIndex, episodes, autoplay, filter });
  useEffect(() => {
    playlistRef.current = { activePlaylist, playlistIndex, episodes, autoplay, filter };
  }, [activePlaylist, playlistIndex, episodes, autoplay, filter]);

  const stopCurrentAudio = () => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.onloadedmetadata = null;
      audioRef.current.oncanplay = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (progressRef.current) {
      cancelAnimationFrame(progressRef.current);
      progressRef.current = null;
    }
  };

  const playEpisode = async (ep) => {
    stopCurrentAudio();

    setAudioLoading(true);
    setAudioError(null);
    setCurrentEpisode(ep);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    const audio = new Audio();
    audio.preload = 'auto';
    audio.volume = muted ? 0 : volume;
    audioRef.current = audio;

    audio.onloadedmetadata = () => {
      if (!isNaN(audio.duration)) setDuration(audio.duration);
    };

    audio.oncanplay = () => {
      setAudioLoading(false);
    };

    audio.onended = () => {
      setIsPlaying(false);
      if (progressRef.current) cancelAnimationFrame(progressRef.current);
      // Record listen
      api.post(`/forge-stories/audio/${lang}/${ep.episode}/listen`).catch(() => {});
      // Auto-advance playlist / filtered list
      const { activePlaylist: pl, playlistIndex: idx, episodes: eps, autoplay: ap, filter: flt } = playlistRef.current;
      if (pl) {
        const nextIdx = idx + 1;
        if (nextIdx < pl.episodes.length) {
          setPlaylistIndex(nextIdx);
          const nextEp = eps.find(e => e.episode === pl.episodes[nextIdx]);
          if (nextEp) {
            setTimeout(() => playEpisode(nextEp), 300);
          }
        } else if (ap && pl.episodes.length > 0) {
          // Autoplay: loop the playlist back to the beginning
          setPlaylistIndex(0);
          const firstEp = eps.find(e => e.episode === pl.episodes[0]);
          if (firstEp) {
            setTimeout(() => playEpisode(firstEp), 300);
          }
        }
      } else if (ap) {
        // No active playlist — advance through the currently filtered list, looping at the end
        const list = flt === 'all' ? eps : eps.filter(e => e.course === flt);
        if (list.length > 0) {
          const curIdx = list.findIndex(e => e.episode === ep.episode);
          const nextEp = curIdx >= 0 && curIdx < list.length - 1 ? list[curIdx + 1] : list[0];
          if (nextEp) {
            setTimeout(() => playEpisode(nextEp), 300);
          }
        }
      }
    };

    audio.onerror = () => {
      // Only show error if this is still the current audio (not replaced)
      if (audioRef.current === audio) {
        setAudioLoading(false);
        setIsPlaying(false);
        setAudioError(`Audio unavailable for Ep. ${ep.episode}`);
        toast.error(`Audio unavailable for Ep. ${ep.episode}`);
      }
    };

    // Set src AFTER attaching handlers
    audio.src = `${BACKEND_URL}/api/forge-stories/audio/${lang}/${ep.episode}`;

    // Wait for enough data to play, then start
    try {
      await new Promise((resolve, reject) => {
        const onReady = () => {
          audio.removeEventListener('canplay', onReady);
          audio.removeEventListener('error', onErr);
          resolve();
        };
        const onErr = () => {
          audio.removeEventListener('canplay', onReady);
          audio.removeEventListener('error', onErr);
          reject(new Error('Load failed'));
        };
        // If already ready
        if (audio.readyState >= 3) { resolve(); return; }
        audio.addEventListener('canplay', onReady);
        audio.addEventListener('error', onErr);
      });

      // Only play if still the current audio
      if (audioRef.current === audio) {
        await audio.play();
        setIsPlaying(true);
        setAudioLoading(false);
        progressRef.current = requestAnimationFrame(updateProgress);
      }
    } catch (err) {
      if (audioRef.current === audio) {
        setAudioLoading(false);
        setIsPlaying(false);
      }
    }
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      if (progressRef.current) cancelAnimationFrame(progressRef.current);
      setIsPlaying(false);
    } else {
      audio.play().then(() => {
        setIsPlaying(true);
        progressRef.current = requestAnimationFrame(updateProgress);
      }).catch(() => {});
    }
  };

  const seekTo = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    if (audioRef.current && duration) {
      audioRef.current.currentTime = (pct / 100) * duration;
      setProgress(pct);
      setCurrentTime((pct / 100) * duration);
    }
  };

  const getPlayList = useCallback(() => {
    if (activePlaylist) {
      return activePlaylist.episodes.map(epNum => episodes.find(e => e.episode === epNum)).filter(Boolean);
    }
    return filter === 'all' ? episodes : episodes.filter(e => e.course === filter);
  }, [activePlaylist, episodes, filter]);

  const skipNext = () => {
    const list = getPlayList();
    const idx = list.findIndex(e => e.episode === currentEpisode?.episode);
    if (idx >= 0 && idx < list.length - 1) playEpisode(list[idx + 1]);
  };

  const skipPrev = () => {
    // If more than 3 seconds in, restart current. Otherwise go previous.
    if (audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      setProgress(0);
      setCurrentTime(0);
      return;
    }
    const list = getPlayList();
    const idx = list.findIndex(e => e.episode === currentEpisode?.episode);
    if (idx > 0) playEpisode(list[idx - 1]);
  };

  const handleVolume = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setMuted(val === 0);
    if (audioRef.current) audioRef.current.volume = val;
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    if (audioRef.current) audioRef.current.volume = next ? 0 : volume;
  };

  const handleSavePlaylist = () => {
    if (!playlistName.trim()) { toast.error('Name required'); return; }
    const epArray = Array.from(selectedForPlaylist).sort((a, b) => a - b);
    if (epArray.length === 0) { toast.error('Select at least one episode'); return; }
    const pl = { id: editingPlaylist?.id || Date.now(), name: playlistName.trim(), episodes: epArray, lang };
    const isEdit = editingPlaylist && editingPlaylist !== 'new';
    const updated = isEdit
      ? playlists.map(p => p.id === editingPlaylist.id ? pl : p)
      : [...playlists, pl];
    savePlaylists(updated);
    setEditingPlaylist(null); setPlaylistName(''); setSelectedForPlaylist(new Set());
    toast.success(isEdit ? 'Playlist updated' : 'Playlist created');
  };

  const handleDeletePlaylist = (pl) => {
    savePlaylists(playlists.filter(p => p.id !== pl.id));
    if (activePlaylist?.id === pl.id) setActivePlaylist(null);
    toast.success('Playlist deleted');
  };

  const startEditPlaylist = (pl) => {
    setEditingPlaylist(pl);
    setPlaylistName(pl.name);
    setSelectedForPlaylist(new Set(pl.episodes));
  };

  const filteredEpisodes = filter === 'all' ? episodes : episodes.filter(e => e.course === filter);
  const isEditing = editingPlaylist !== null;

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4" style={{ paddingBottom: currentEpisode ? '180px' : '70px' }} data-testid="listening-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-700 flex items-center justify-center">
            <Headphones className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Listening</h1>
            <p className="text-xs text-muted-foreground">{episodes.length} episodes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={lang}
            onChange={(e) => { setLang(e.target.value); navigate(`/listening/${e.target.value}`, { replace: true }); }}
            className="px-2 py-1.5 rounded-lg bg-muted border border-border text-sm"
            data-testid="listening-lang-select"
          >
            {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={() => navigate(`/forge-stories/${lang}`)} className="gap-1">
            <BookOpen className="h-4 w-4" /> Stories
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={filter === 'all' && !activePlaylist ? 'default' : 'ghost'} onClick={() => { setFilter('all'); setActivePlaylist(null); }} className="text-xs">All</Button>
        {[1, 2, 3, 4].map(c => (
          <Button key={c} size="sm" variant={filter === c && !activePlaylist ? 'default' : 'ghost'} onClick={() => { setFilter(c); setActivePlaylist(null); }} className="text-xs">C{c}</Button>
        ))}
        <div className="flex-1" />
        <Button
          size="sm"
          variant={autoplay ? 'default' : 'outline'}
          onClick={() => setAutoplay(a => !a)}
          className="gap-1 text-xs"
          title={autoplay ? 'Autoplay on — will loop playlist when done' : 'Autoplay off'}
          data-testid="autoplay-toggle"
        >
          <Repeat className="h-3 w-3" /> Autoplay {autoplay ? 'On' : 'Off'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setEditingPlaylist('new'); setPlaylistName(''); setSelectedForPlaylist(new Set()); }} className="gap-1 text-xs">
          <Plus className="h-3 w-3" /> New Playlist
        </Button>
      </div>

      {/* Saved playlists */}
      {playlists.filter(p => p.lang === lang).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Playlists:</span>
          {playlists.filter(p => p.lang === lang).map(pl => (
            <div key={pl.id} className="flex items-center gap-1">
              <Button
                size="sm"
                variant={activePlaylist?.id === pl.id ? 'default' : 'outline'}
                onClick={() => { setActivePlaylist(activePlaylist?.id === pl.id ? null : pl); setPlaylistIndex(0); }}
                className="text-xs gap-1 h-7"
              >
                <List className="h-3 w-3" /> {pl.name} ({pl.episodes.length})
              </Button>
              <button onClick={() => startEditPlaylist(pl)} className="text-muted-foreground hover:text-foreground p-0.5"><Pencil className="h-3 w-3" /></button>
              <button onClick={() => handleDeletePlaylist(pl)} className="text-red-400 hover:text-red-300 p-0.5"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Playlist editor */}
      {isEditing && (
        <Card className="border-indigo-500/30 bg-indigo-950/20">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={playlistName}
                onChange={e => setPlaylistName(e.target.value)}
                placeholder="Playlist name"
                className="flex-1 px-3 py-1.5 rounded-lg bg-muted border border-border text-sm"
                data-testid="playlist-name-input"
              />
              <Button size="sm" onClick={handleSavePlaylist} className="gap-1"><Save className="h-3 w-3" /> Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditingPlaylist(null); setSelectedForPlaylist(new Set()); }}><X className="h-3 w-3" /></Button>
            </div>
            <p className="text-xs text-muted-foreground">Select episodes ({selectedForPlaylist.size} selected):</p>
            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
              {episodes.map(ep => (
                <button
                  key={ep.episode}
                  onClick={() => {
                    const next = new Set(selectedForPlaylist);
                    next.has(ep.episode) ? next.delete(ep.episode) : next.add(ep.episode);
                    setSelectedForPlaylist(next);
                  }}
                  className={`px-2 py-1 rounded text-xs border transition-colors ${
                    selectedForPlaylist.has(ep.episode)
                      ? 'bg-indigo-600 border-indigo-500 text-white'
                      : 'bg-muted border-border text-muted-foreground hover:border-indigo-400'
                  }`}
                >
                  {ep.episode}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Episode list */}
      <div className="space-y-1" data-testid="episode-list">
        {(activePlaylist
          ? activePlaylist.episodes.map(epNum => episodes.find(e => e.episode === epNum)).filter(Boolean)
          : filteredEpisodes
        ).map(ep => {
          const isCurrent = currentEpisode?.episode === ep.episode;
          return (
            <div
              key={ep.episode}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all cursor-pointer ${isCurrent ? 'bg-indigo-950/40 border border-indigo-500/30' : 'hover:bg-slate-800/50'}`}
              data-testid={`listen-ep-${ep.episode}`}
              onClick={() => isCurrent ? togglePlayPause() : playEpisode(ep)}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isCurrent && isPlaying ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-indigo-600 hover:text-white'}`}>
                {isCurrent && audioLoading ? <Loader2 className="h-4 w-4 animate-spin" />
                  : isCurrent && isPlaying ? <Pause className="h-4 w-4" />
                  : <Play className="h-4 w-4 ml-0.5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isCurrent ? 'text-indigo-300' : ''}`}>Ep. {ep.episode}: {ep.title}</p>
                <p className="text-xs text-muted-foreground">C{ep.course} — {ep.theme} — {ep.word_count}w</p>
              </div>
              {ep.listen_count > 0 && <span className="text-[10px] text-emerald-400 flex items-center gap-0.5 flex-shrink-0"><Repeat className="h-3 w-3" /> {ep.listen_count}</span>}
              {isAdmin && (
                <a
                  href={`${BACKEND_URL}/api/forge-stories/audio/${lang}/${ep.episode}`}
                  download={`es_ep${ep.episode}.mp3`}
                  onClick={(e) => e.stopPropagation()}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-indigo-400 hover:bg-indigo-500/10 transition-colors flex-shrink-0"
                  title="Download audio"
                  data-testid={`download-ep-${ep.episode}`}
                >
                  <Download className="h-4 w-4" />
                </a>
              )}
            </div>
          );
        })}
        {episodes.length === 0 && <p className="text-center text-muted-foreground py-8 text-sm">No audio available for this language yet.</p>}
      </div>

      {/* Audio error */}
      {audioError && (
        <div className="text-center text-red-400 text-sm py-2">{audioError}</div>
      )}

      {/* Player bar */}
      {currentEpisode && (
        <div className="fixed left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 bottom-[52px] md:bottom-0" data-testid="audio-player">
          {/* Seek bar — wider hit area */}
          <div
            className="h-6 flex items-center cursor-pointer group px-0"
            onClick={seekTo}
            data-testid="seek-bar"
          >
            <div className="w-full h-1 bg-slate-800 relative rounded-full group-hover:h-2 transition-all">
              <div
                className="h-full bg-indigo-500 rounded-full transition-[width] group-hover:bg-indigo-400"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `${progress}%`, transform: `translate(-50%, -50%)` }}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 pb-3 max-w-3xl mx-auto">
            {/* Episode info */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-indigo-300 truncate">
                Ep. {currentEpisode.episode}: {currentEpisode.title}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {formatTime(currentTime)} / {formatTime(duration)}
              </p>
            </div>

            {/* Transport controls */}
            <div className="flex items-center gap-2">
              <button onClick={skipPrev} className="text-slate-400 hover:text-white p-1" data-testid="skip-prev">
                <SkipBack className="h-5 w-5" />
              </button>
              <button
                onClick={togglePlayPause}
                disabled={audioLoading}
                className="w-11 h-11 rounded-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 flex items-center justify-center text-white transition-colors"
                data-testid="play-pause-btn"
              >
                {audioLoading ? <Loader2 className="h-5 w-5 animate-spin" />
                  : isPlaying ? <Pause className="h-5 w-5" />
                  : <Play className="h-5 w-5 ml-0.5" />}
              </button>
              <button onClick={skipNext} className="text-slate-400 hover:text-white p-1" data-testid="skip-next">
                <SkipForward className="h-5 w-5" />
              </button>
            </div>

            {/* Volume */}
            <div className="hidden sm:flex items-center gap-1.5 w-28">
              <button onClick={toggleMute} className="text-slate-400 hover:text-white p-1" data-testid="mute-btn">
                {muted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={handleVolume}
                className="w-full h-1 accent-indigo-500 cursor-pointer"
                data-testid="volume-slider"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
